import { Component, Input, Output, EventEmitter, inject, OnDestroy, OnInit, signal, computed, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';

// Services
import { AuthService as AuthNewService } from '@core/services/auth-new.service';
import { EnhancedThemeService } from '@core/theme/services/enhanced-theme.service';

// UI Components
import { TooltipDirective } from '@shared/components/ui/tooltip';
import { PermissionService } from '@shared/permission-service.ts/resorces-permission.service';

interface SubNavigationItem {
  title: string;
  route: string;
}

interface NavigationItem {
  id?: any;
  title: string;
  icon: string;
  route: string;
  children?: SubNavigationItem[];
  isActive?: boolean;
}

interface ResourcePermission {
  id: number;
  UserID: number | null;
  RoleID: number | null;
  GroupID: number | null;
  ResourceID: number;
  CanView: number;
  CanEdit: number;
  CanDelete: number;
  IsActive: number;
  IsDeleted: number;
  CreatedDate: string;
  CreatedBy: number;
  UpdatedDate: string;
  UpdatedBy: number;
}

interface MenuItem {
  id: number;
  Name: string;
  Description: string;
  DisplayName: string;
  ParentId: number | null;
  UrlRoute: string | null;
  Params: any;
  NavIconClass: string;
  HideMenu: number;
  SortOrder: number;
  ResourceTypeID: number;
  IsActive: number;
  IsDeleted: number;
  UpdatedDate: string;
  UpdatedBy: number;
  flag: number;
  resource_permission: ResourcePermission[];
  children?: MenuItem[];
  benchmark: any;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, TooltipDirective],
  template: `
    <!-- Desktop Sidebar -->
    <aside class="sidebar-container desktop-sidebar" [class.open]="isOpen">
      <div class="sidebar-content">
        <!-- Main Navigation Icons -->
        <nav class="navigation-main">
          <div class="nav-icons-container">
            <!-- Skeleton Loader -->
            @if (isLoadingMenu) {
              @for (item of [1,2,3,4,5,6]; track $index) {
                <div class="nav-item-wrapper skeleton-wrapper">
                  <div class="skeleton-nav-icon">
                    <div class="skeleton-icon-container">
                      <div class="skeleton-icon"></div>
                    </div>
                  </div>
                </div>
              }
            }
            
            <!-- Actual Navigation Items -->
            @if (!isLoadingMenu) {
              @for (item of dynamicNavigationItems; track $index) {
                <div
                  class="nav-item-wrapper"
                  (mouseenter)="handleMouseEnter(item, $index, $event)"
                  (mouseleave)="handleMouseLeave()"
                >
                  <!-- Navigation Icon -->
                  <a
                    [routerLink]="item.route"
                    [class]="getIconClasses(isActive(item), item)"
                    [appTooltip]="shouldShowTooltip(item) ? getTooltipText(item) : ''"
                    tooltipPosition="right"
                    class="nav-icon-link"
                    (click)="onNavigationClick($event, item)"
                  >
                    <div class="icon-container" [class.active]="isActive(item) || isSubmenuOpen(item)">
                      @if (item.icon?.startsWith('https://')) {
                        <img [src]="item.icon" alt="{{ item.title }}" class="feather-icon">
                      } @else {
                        <i [attr.data-feather]="item.icon" class="feather-icon"></i>
                      }
                      <!-- Small indicator for items with children -->
                      @if (item.children && item.children.length > 0) {
                        <!-- <div class="children-indicator">
                          <svg width="4" height="4" viewBox="0 0 4 4" fill="currentColor">
                            <circle cx="2" cy="2" r="1.5"/>
                          </svg>
                        </div> -->
                      }
                    </div>
                  </a>

                  <!-- Submenu for items with children -->
                      @if (item.children && item.children.length > 1 && hoveredItem === item) {
                    <div 
                      class="submenu-container"
                      [style.top.px]="submenuTopPosition"
                      [style.left.px]="submenuLeftPosition"
                      (mouseenter)="handleSubmenuEnter()"
                      (mouseleave)="handleSubmenuLeave()"
                    >
                      <div class="submenu-content">
                        <div class="submenu-items-scroll">
                          @for (child of item.children; track $index) {
                            <a
                              [routerLink]="child.route"
                              class="submenu-item"
                              [class.active]="router.url.includes(child.route)"
                            >
                              {{ child.title }}
                            </a>
                          }
                        </div>
                      </div>
                    </div>
                  }
                </div>
              }
            }
          </div>
        </nav>

        <!-- Bottom Settings Icons -->
        <div class="navigation-bottom">
          <!-- Dynamic Static Settings with permission checks -->
          @for (dynamicSetting of dynamicStaticSettings; track $index) {
            <button
              class="settings-btn"
              [class.active]="isDynamicSettingActive(dynamicSetting)"
              [appTooltip]="dynamicSetting.DisplayName"
              tooltipPosition="right"
              (click)="openDynamicSetting(dynamicSetting)"
            >
              <div class="icon-container" [class.active]="isDynamicSettingActive(dynamicSetting)">
                @if (dynamicSetting.NavIconClass?.startsWith('https://')) {
                  <img [src]="dynamicSetting.NavIconClass" alt="{{ dynamicSetting.DisplayName }}" class="feather-icon">
                } @else {
                  <i [attr.data-feather]="getIconForMenu(dynamicSetting.NavIconClass || dynamicSetting.Name)" class="feather-icon"></i>
                }
              </div>
            </button>
          }
        </div>
      </div>
    </aside>

    <!-- Mobile Sidebar Drawer -->
    @if (isOpen) {
      <div class="mobile-sidebar-overlay" (click)="closeMobileSidebar()">
        <aside class="mobile-sidebar" (click)="$event.stopPropagation()">
          <!-- Mobile Header -->
          <div class="mobile-sidebar-header">
            <button class="mobile-back-btn" (click)="closeMobileSidebar()">
              
              <img  src="assets/images/back-btn.png" alt="logo" class="mobile-logo">
            </button>
            <span class="mobile-sidebar-title">Menu</span>
          </div>
          <div class='mobile-search-container-wrapper'>

          <!-- User Greeting Card -->
          <div class="mobile-user-card">
            <div class="mobile-avatar-wrapper">
              <div class="mobile-user-avatar" >
                <span>{{ getUserInitials() }}</span>
              </div>
              
            </div>
            <span class="mobile-user-greeting">Hello, {{ currentUserName() }}</span>
          </div>

          <!-- Main Menu Label -->
          <div class="mobile-menu-label">Main Menu</div>

          <!-- Mobile Navigation Items -->
          <nav class="mobile-nav-container">
            @if (isLoadingMenu) {
              @for (item of [1,2,3,4]; track $index) {
                <div class="mobile-nav-item-skeleton"></div>
              }
            }
            
            @if (!isLoadingMenu) {
              @for (item of dynamicNavigationItems; track $index) {
                <div class="mobile-nav-item-wrapper">
                  <!-- Nav Item -->
                  <button
                    class="mobile-nav-item"
                    [class.active]="isActive(item)"
                    [class.expanded]="expandedMobileItem() === item.title"
                    (click)="onMobileNavClick(item)"
                  >
                    <div class="mobile-nav-icon">
                      @if (item.icon?.startsWith('https://')) {
                        <img [src]="item.icon" alt="{{ item.title }}" class="feather-icon">
                      } @else {
                        <i [attr.data-feather]="item.icon" class="feather-icon"></i>
                      }
                    </div>
                    <span class="mobile-nav-label">{{ item.title }}</span>
                    @if (item.children && item.children.length > 1) {
                      <svg 
                        class="mobile-nav-chevron" 
                        [class.rotated]="expandedMobileItem() === item.title"
                        width="20" height="20" viewBox="0 0 24 24" fill="none"
                      >
                        <path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    } @else {
                      <svg class="mobile-nav-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    }
                  </button>

                  <!-- Accordion Submenu -->
                    @if (item.children && item.children.length > 1 && expandedMobileItem() === item.title) {
                    <div class="mobile-submenu">
                      @for (child of item.children; track $index) {
                        <a
                          [routerLink]="child.route"
                          class="mobile-submenu-item"
                          [class.active]="router.url.includes(child.route)"
                          (click)="onMobileSubNavClick()"
                        >
                          {{ child.title }}
                        </a>
                      }
                    </div>
                  }
                </div>
              }
            }
          </nav>

          <!-- Bottom Section -->
          <div class="mobile-sidebar-bottom">
            <!-- Dark Mode Toggle -->
            <div class="mobile-setting-item">
              <div class="mobile-setting-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <span class="mobile-setting-label">Dark Mode</span>
              <label class="mobile-toggle">
                <input type="checkbox" [checked]="isDarkMode()" (change)="toggleDarkMode()">
                <span class="mobile-toggle-slider"></span>
              </label>
            </div>

            <!-- Logout -->
            <button class="mobile-logout-btn" (click)="logout()">
              <div class="mobile-setting-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <span class="mobile-setting-label">Logout</span>
            </button>

            <!-- Dynamic Settings (e.g., Settings) -->
            @for (dynamicSetting of dynamicStaticSettings; track $index) {
              <button
                class="mobile-setting-item mobile-setting-btn"
                (click)="openDynamicSettingMobile(dynamicSetting)"
              >
                <div class="mobile-setting-icon">
                  @if (dynamicSetting.NavIconClass?.startsWith('https://')) {
                    <img [src]="dynamicSetting.NavIconClass" alt="{{ dynamicSetting.DisplayName }}" class="feather-icon">
                  } @else {
                    <i [attr.data-feather]="getIconForMenu(dynamicSetting.NavIconClass || dynamicSetting.Name)" class="feather-icon"></i>
                  }
                </div>
                <span class="mobile-setting-label">{{ dynamicSetting.DisplayName }}</span>
                <svg class="mobile-nav-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            }
          </div>
          </div>

        </aside>
      </div>
    }
  `,
  styles: [`
    :host {
      display: block;
      height: 100%;
      --sidebar-skeleton-base: #e2e8f0;
      --sidebar-skeleton-highlight: #cfd4e8;
    }

    .sidebar-container {
      width: 95px;
      height: 100%;
      background-color: var(--colors-background-card);
      border-radius: 17px;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: center;
      transition: var(--component-transition);
      position: relative;
    }

    .sidebar-content {
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: center;
      padding: 20px;
      gap: 7px;
    }
    :host-context(.dark),
    :host-context([data-theme='dark']) {
      --sidebar-skeleton-base: #0e1018;
      --sidebar-skeleton-highlight: #1f2230;

      .sidebar-content {
        background-color: #121212;
        border-radius: 17px;
        gap: 7px;
      }

      /* Dark mode: hover uses interactive-hover color (subtle); active keeps its own bg */
      .nav-icon-link:hover:not(.active),
      .nav-item-wrapper:hover .nav-icon-link:not(.active) {
        background-color: var(--colors-interactive-hover);
      }
    }
    .navigation-main {
      display: flex;
      flex-direction: column;
      align-items: center;
      flex: 1;
      overflow: hidden;
    }

    .nav-icons-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      width: 100%;
      overflow-y: auto;
      overflow-x: visible;
      max-height: 100%;
      padding-right: 5px;
      scrollbar-width: none;
 
    }

    .nav-icons-container::-webkit-scrollbar {
      width: 3px;
    }

    .nav-icons-container::-webkit-scrollbar-track {
      background: transparent;
    }

    .nav-icons-container::-webkit-scrollbar-thumb {
      background: var(--colors-border-primary);
      border-radius: 10px;
    }

    .nav-icons-container::-webkit-scrollbar-thumb:hover {
      background: var(--colors-border-secondary);
    }

    .nav-item-wrapper {
      position: relative;
      width: 100%;
      display: flex;
      justify-content: center;
      margin-bottom: 0px;
    }

    .nav-icon-link {
      width: 55px;
      height: 55px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 10px;
      transition: var(--component-transition);
      text-decoration: none;
      position: relative;
      gap: 16.5px;
      cursor: pointer;
    }

    .icon-container {
      width: 27px;
      height: 27px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--colors-text-muted);
      transition: var(--component-transition);
    }

    .feather-icon {
      width: 23px;
      height: 23px;
      stroke: currentColor;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
      fill: none;
      /* Default: render as black regardless of source SVG color */
      filter: brightness(0);
      transition: filter 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    }

    /* Active state: white icon (dark background is set on .nav-icon-link.active) */
    .icon-container.active .feather-icon {
      filter: brightness(0) invert(1);
    }

    /* Active takes precedence over hover */
    .nav-icon-link.active .feather-icon,
    .icon-container.active .feather-icon {
      filter: brightness(0) invert(1) !important;
    }

    .icon-container.active {
      color: var(--colors-text-inverse);
    }

    .children-indicator {
      position: absolute;
      bottom: -2px;
      right: -2px;
      width: 8px;
      height: 8px;
      background-color: var(--colors-primary);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 6px;
      border: 1.5px solid var(--colors-background-card);
      opacity: 0.8;
    }

    .nav-icon-link:hover .children-indicator {
      opacity: 1;
    }

    .nav-icon-link:hover {
      background-color: var(--colors-text-primary);
    }

    .nav-icon-link:hover .icon-container {
      color: var(--colors-text-inverse);
    }

    /* Hover shows white icon (same as active state) */
    .nav-icon-link:hover .feather-icon {
      filter: brightness(0) invert(1) !important;
    }

    .nav-icon-link.active {
      background-color: var(--colors-text-primary);
    }

    .nav-icon-link.active .icon-container {
      color: var(--colors-text-inverse);
    }

    /* Keep icon highlighted when submenu is open */
    .nav-item-wrapper:hover .nav-icon-link {
      background-color: var(--colors-text-primary);
    }

    /* Active item stays active-colored when hovered */
    .nav-item-wrapper:hover .nav-icon-link.active {
      background-color: var(--colors-text-primary);
    }

    .nav-item-wrapper:hover .nav-icon-link .icon-container {
      color: var(--colors-text-inverse);
    }

    /* All hovered/active items show white icon */
    .nav-item-wrapper:hover .nav-icon-link .feather-icon,
    .nav-item-wrapper:hover .nav-icon-link.active .feather-icon,
    .nav-icon-link.active:hover .feather-icon {
      filter: brightness(0) invert(1) !important;
    }

    /* Submenu Styles */
    .submenu-container {
      position: fixed;
      z-index: 9999;
      pointer-events: auto;
      animation: submenuFadeIn 0.1s ease-out;
    }

    /* Bridge element to prevent gap hover issues */
    .submenu-container::before {
      content: '';
      position: absolute;
      right: 100%;
      top: 0;
      bottom: 0;
      width: 20px;
      pointer-events: auto;
      /* Uncomment below to visualize the bridge during development */
      /* background: rgba(255, 0, 0, 0.1); */
    }

    .submenu-content {
      background-color: var(--colors-background-card);
      border-radius: 12px;
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15), 0 4px 6px rgba(0, 0, 0, 0.1);
      padding: 0;
      min-width: 200px;
      max-width: 250px;
      border: 1px solid var(--colors-border-primary);
      backdrop-filter: blur(10px);
      max-height: 300px;
      overflow: hidden;
    }

    .submenu-items-scroll {
      max-height: 300px;
      overflow-y: auto;
      padding: 8px 0;
    }

    .submenu-items-scroll::-webkit-scrollbar {
      width: 4px;
    }

    .submenu-items-scroll::-webkit-scrollbar-track {
      background: transparent;
      margin: 8px 0;
    }

    .submenu-items-scroll::-webkit-scrollbar-thumb {
      background: var(--colors-border-primary);
      border-radius: 10px;
    }

    .submenu-items-scroll::-webkit-scrollbar-thumb:hover {
      background: var(--colors-border-secondary);
    }

    @keyframes submenuFadeIn {
      from {
        opacity: 0;
        transform: translateX(-4px) scale(0.98);
      }
      to {
        opacity: 1;
        transform: translateX(0) scale(1);
      }
    }

    .submenu-item {
      display: block;
      padding: 12px 16px;
      color: var(--colors-text-primary);
      text-decoration: none;
      font-size: 14px;
      font-weight: 400;
      transition: var(--component-transition);
      border-radius: 8px;
      margin: 0 8px;
      cursor: pointer;
    }

    .submenu-item:hover {
      background-color: var(--colors-interactive-hover);
      color: var(--colors-text-primary);
    }

    .submenu-item.active {
      background-color: var(--colors-background-secondary);
      color: var(--colors-text-primary);
      font-weight: 500;
    }

    .navigation-bottom {
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      align-items: center;
      width: 55px;
      margin-top: auto;
      gap: 8px;
      padding-bottom: 20px;
    }

    .settings-btn {
      width: 55px;
      height: 55px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 10px;
      background-color: transparent;
      border: none;
      cursor: pointer;
      transition: var(--component-transition);
    }

    .settings-btn:hover {
      background-color: var(--colors-interactive-hover);
    }

    .settings-btn .icon-container {
      color: var(--colors-text-muted);
      width: 25px;
      height: 25px;
    }

    .settings-btn:hover .icon-container {
      color: var(--colors-text-secondary);
    }

    .settings-btn.active {
      background-color: var(--colors-text-primary);
    }

    .settings-btn.active .icon-container {
      color: var(--colors-text-inverse);
    }

    /* Hide desktop sidebar on mobile */
    @media (max-width: 768px) {
      .desktop-sidebar {
        display: none !important;
      }
    }

    /* Mobile Sidebar Styles */
    .mobile-sidebar-overlay {
      display: none;
    }

    @media (max-width: 768px) {
      .mobile-sidebar-overlay {
        display: block;
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 2000;
        animation: fadeIn 0.2s ease-out;
      }

      .mobile-search-container-wrapper{
          flex: 1;
          min-height: 0;
          background: #F0F4F5;
          padding: 15px;
          border-radius: 40px 40px 0 0;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          /* Safari mobile fix for border-radius rendering */
          -webkit-mask-image: -webkit-radial-gradient(white, black);
          mask-image: radial-gradient(white, black);
          -webkit-backface-visibility: hidden;
          backface-visibility: hidden;
        }
      .mobile-sidebar {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        max-width: 100%;
        /* Use 100dvh (dynamic viewport height) for iOS to exclude browser chrome */
        height: 100vh;
        height: 100dvh;
        z-index: 2001;
        display: flex;
        flex-direction: column;
        animation: slideInLeft 0.3s ease-out;
        overflow: hidden;
        box-sizing: border-box;
        cursor: pointer;
      }

      .mobile-sidebar > * {
        cursor: default;
      }

      @keyframes slideInLeft {
        from {
          transform: translateX(-100%);
        }
        to {
          transform: translateX(0);
        }
      }

      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      .mobile-sidebar-header {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 16px 20px;
      }

      .mobile-back-btn {
        width: 40px;
        height: 40px;
        position: absolute;
        left: 20px;
        top: 50%;
        transform: translateY(-50%);
        display: flex;
        align-items: center;
        justify-content: center;
        background: #292929;
        border: none;
        border-radius: 50%;
        cursor: pointer;
        color: white;
      }

      .mobile-sidebar-title {
        font-size: 16px;
        font-weight: 500;
        color: var(--colors-text-primary);
      }

      .mobile-user-card {
                display: flex;
        align-items: center;
        gap: 12px;
        /* margin: 20px; */
        padding: 15px;
        background: rgba(71, 33, 147, 0.4);
        border-radius: 30px;
      }

      .mobile-avatar-wrapper {
        display: flex;
        align-items: center;
        gap: 6px;
      }

      .mobile-user-avatar {
        width: 26px;
        height: 26px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 10px;
        font-weight: 400;
        color: rgba(71, 33, 147, 1);
        background: white;
      }

      .mobile-avatar-arrow {
        display: flex;
        align-items: center;
        justify-content: center;
        color: #1a1a1a;
      }

      .mobile-user-greeting {
        font-size: 18px;
        font-weight: 300;
        color: white;
        line-height: 1;
      }

      .mobile-menu-label {
                padding: 20px 0;
        padding-bottom: 15px !important;
        font-size: 13px;
        font-weight: 300;
        color: var(--colors-text-muted);
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .mobile-nav-container {
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        padding: 0;
        /* Account for fixed bottom bar height (~120px) plus iOS home indicator */
        padding-bottom: calc(160px + env(safe-area-inset-bottom, 0px));
        -webkit-overflow-scrolling: touch;
        -ms-overflow-style: none;
        overscroll-behavior: contain;
        min-height: 0;
      }

      .mobile-nav-item-wrapper {
        margin-bottom: 4px;
      }

      .mobile-nav-item {
        display: flex;
        align-items: center;
        gap: 12px;
        width: 100%;
        padding: 14px 0px;
        background: transparent;
        border: none;
        border-radius: 12px;
        cursor: pointer;
        transition: background-color 0.2s ease;
        text-align: left;
      }

      .mobile-nav-item:hover,
      .mobile-nav-item:active {
        background-color: var(--colors-interactive-hover);
      }

      .mobile-nav-item.active {
        background-color: var(--colors-interactive-hover);
      }

      .mobile-nav-icon {
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--colors-text-secondary);
      }

      .mobile-nav-icon .feather-icon {
        width: 20px;
        height: 20px;
      }

      .mobile-nav-label {
        flex: 1;
        font-size: 15px;
        font-weight: 400;
        color: var(--colors-text-primary);
      }

      .mobile-nav-chevron,
      .mobile-nav-arrow {
        color: var(--colors-text-muted);
        transition: transform 0.2s ease;
      }

      .mobile-nav-chevron.rotated {
        transform: rotate(180deg);
      }

      .mobile-submenu {
        padding: 4px 0 4px 42px;
        animation: expandDown 0.2s ease-out;
      }

      @keyframes expandDown {
        from {
          opacity: 0;
          max-height: 0;
        }
        to {
          opacity: 1;
          max-height: 500px;
        }
      }

      .mobile-submenu-item {
        display: block;
        padding: 12px 0px;
        font-size: 14px;
        color: var(--colors-text-secondary);
        text-decoration: none;
        border-radius: 8px;
        transition: background-color 0.2s ease;
      }

      .mobile-submenu-item:hover,
      .mobile-submenu-item:active {
        background-color: var(--colors-interactive-hover);
      }

      .mobile-submenu-item.active {
        color: var(--colors-text-primary);
        font-weight: 500;
      }

      .mobile-nav-item-skeleton {
        height: 52px;
        margin: 4px 0;
        background: var(--sidebar-skeleton-base);
        border-radius: 12px;
        animation: skeleton-pulse 1.5s ease-in-out infinite;
      }

      .mobile-sidebar-bottom {
        padding: 12px 15px;
        padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
        border-top: 1px solid var(--colors-border-muted);
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        background: #F0F4F5;
        z-index: 10;
        box-sizing: border-box;
      }

      .mobile-setting-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 14px 0;
        width: 100%;
      }

      .mobile-setting-icon {
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--colors-text-secondary);
      }

      .mobile-setting-label {
        flex: 1;
        font-size: 15px;
        color: var(--colors-text-primary);
        min-width: 0;
      }

      .mobile-toggle {
        position: relative;
        width: 48px;
        height: 28px;
        flex-shrink: 0;
      }

      .mobile-toggle input {
        opacity: 0;
        width: 0;
        height: 0;
      }

      .mobile-toggle-slider {
        position: absolute;
        cursor: pointer;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: var(--colors-border-primary);
        transition: 0.3s;
        border-radius: 28px;
      }

      .mobile-toggle-slider:before {
        position: absolute;
        content: "";
        height: 22px;
        width: 22px;
        left: 3px;
        bottom: 3px;
        background-color: white;
        transition: 0.3s;
        border-radius: 50%;
      }

      .mobile-toggle input:checked + .mobile-toggle-slider {
        background-color: var(--colors-brand-primary, #6366f1);
      }

      .mobile-toggle input:checked + .mobile-toggle-slider:before {
        transform: translateX(20px);
      }

      .mobile-logout-btn {
        display: flex;
        align-items: center;
        gap: 12px;
        width: 100%;
        padding: 14px 0;
        background: transparent;
        border: none;
        cursor: pointer;
        text-align: left;
      }

      .mobile-setting-btn {
        display: flex;
        align-items: center;
        gap: 12px;
        width: 100%;
        padding: 14px 0;
        background: transparent;
        border: none;
        cursor: pointer;
        text-align: left;
        border-top: 1px solid var(--colors-border-muted);
      }

      .mobile-setting-btn .feather-icon {
        width: 20px;
        height: 20px;
        stroke: currentColor;
        stroke-width: 2;
        stroke-linecap: round;
        stroke-linejoin: round;
        fill: none;
      }

      .mobile-setting-btn .mobile-nav-arrow {
        color: var(--colors-text-muted);
        margin-left: auto;
      }
    }

    @media (min-width: 769px) and (max-width: 1024px) {
      .sidebar-container {
        width: 85px;
      }

      .nav-icon-link {
        width: 50px;
        height: 50px;
      }

      .settings-btn {
        width: 50px;
        height: 50px;
      }

      .navigation-bottom {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        align-items: center;
        gap: 6px;
        padding: 8px;
      }
    }

    .nav-icon-link:focus,
    .settings-btn:focus {
      outline: 2px solid #3b82f6;
      outline-offset: 2px;
    }

    .nav-icon-link:focus:not(:focus-visible),
    .settings-btn:focus:not(:focus-visible) {
      outline: none;
    }

    .nav-icon-link,
    .icon-container,
    .submenu-container,
    .settings-btn {
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    }

    /* Skeleton Loader Styles */
    .skeleton-wrapper {
      position: relative;
      width: 100%;
      display: flex;
      justify-content: center;
      margin-bottom: 10px;
    }

    .skeleton-nav-icon {
      width: 55px;
      height: 55px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 10px;
      background-color: var(--sidebar-skeleton-base);
      animation: skeleton-pulse 1.5s ease-in-out infinite;
    }

    .skeleton-icon-container {
      width: 27px;
      height: 27px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .skeleton-icon {
      width: 23px;
      height: 23px;
      background-color: var(--sidebar-skeleton-highlight);
      border-radius: 4px;
      animation: skeleton-pulse 1.5s ease-in-out infinite;
      animation-delay: 0.2s;
    }

    @keyframes skeleton-pulse {
      0%, 100% {
        opacity: 0.4;
      }
      50% {
        opacity: 0.8;
      }
    }

    /* Skeleton loader responsive adjustments */
    @media (max-width: 768px) {
      .skeleton-nav-icon {
        width: 55px;
        height: 55px;
      }
    }

    @media (min-width: 769px) and (max-width: 1024px) {
      .skeleton-nav-icon {
        width: 50px;
        height: 50px;
      }
    }

    /* Dark mode: all icons always white */
    :host-context(.dark) .feather-icon {
      filter: brightness(0) invert(1);
    }

    :host-context(.dark) .icon-container.active .feather-icon,
    :host-context(.dark) .nav-icon-link.active .feather-icon {
      filter: brightness(0) invert(1) !important;
    }

    :host-context(.dark) {
      .mobile-search-container-wrapper, .mobile-sidebar-bottom {
        background-color: #22232A;
      }
          .mobile-search-input-container{
            background-color:black
          }


          .mobile-sidebar{
            background-color:var(--colors-background-primary);
          }

          .mobile-user-avatar{
            background: #292929;
            color: #ffffff;
          }
          .settings-btn.active{
                background-color: var(--colors-background-secondary);
      color: var(--colors-text-primary);
          }

      .mobile-search-input-container {
        background-color: black;
      }
      .mobile-sidebar {
        background-color: var(--colors-background-primary);
      }
      .mobile-user-avatar {
        background: #292929;
        color: #ffffff;
      }
    }
  `],
})
export class SidebarComponent implements OnInit, OnDestroy {
  private _isOpen = false;
  @Input()
  set isOpen(value: boolean) {
    this._isOpen = value;
    if (value) {
      // Reinitialize feather icons when sidebar opens
      setTimeout(() => this.initializeFeatherIcons(), 50);
    }
  }
  get isOpen(): boolean {
    return this._isOpen;
  }
  @Input() navigationItemsInput?: NavigationItem[];
  @Output() toggleSidebar = new EventEmitter<void>();
  @Output() closeRequest = new EventEmitter<void>();

  authNewService = inject(AuthNewService);
  themeService = inject(EnhancedThemeService);
  router = inject(Router);
  private cdr = inject(ChangeDetectorRef);

  hoveredItem: NavigationItem | null = null;
  private hoverTimeout: any = null;
  hoveredItemIndex: number = -1;
  submenuTopPosition: number = 0;
  submenuLeftPosition: number = 120;

  sidebarMenuPermission: MenuItem[] = [];
  dynamicNavigationItems: NavigationItem[] = [];
  dynamicStaticSettings: MenuItem[] = []; // Settings to show in static area
  isLoadingMenu = true;

  // Mobile-specific state
  expandedMobileItem = signal<string | null>(null);

  // Make isDarkMode a computed signal that reacts to theme service changes
  isDarkMode = computed(() => this.themeService.effectiveTheme() === 'dark');

  // Current user name for greeting
  currentUserName = computed(() => {
    const user = this.authNewService.user();
    return user?.name?.split(' ')[0] || 'User';
  });

  // Icon mapping for different menu items
  private iconMapping: { [key: string]: string } = {
    'M_SM_FULFILLMENT': 'package',
    'M_SM_PROVISIONING': 'truck',
    'M_SM_PACKAGING': 'box',
    'DASHBOARD': 'home',
    'ORDERS': 'shopping-cart',
    'PORTING': 'phone',
    'SUBSCRIPTION': 'credit-card',
    'TICKETS': 'help-circle',
    'BULK_PROCESS': 'layers',
    'PAYMENTS': 'dollar-sign',
    'REPORTS': 'bar-chart',
    'BI_BOXES': 'archive',
    'SETTINGS': 'settings',
    'RENEWAL_REPORTS': 'refresh-cw',
    'M_SM_RENEWAL_REPORTS': 'refresh-cw'
  };

  constructor(private permissionService: PermissionService) { }

  ngOnInit(): void {
    this.loadDynamicMenus();

    // Initialize feather icons after view init
    setTimeout(() => {
      this.initializeFeatherIcons();
    }, 100);
  }

  private async loadDynamicMenus(): Promise<void> {
    try {
      this.isLoadingMenu = true;
      await this.permissionService.updatePermission();
      const allCategories: any[] = await this.permissionService.getAllResourceCategories();

      allCategories.forEach((category, index) => {
        // console.log(`${index}: "${category.Name}"`);
      });

      const menusCategory = allCategories.find(
        category => category.Name === 'MENUS'
      );

      if (menusCategory) {
        this.sidebarMenuPermission = menusCategory?.resource_types?.[0]?.resources || [];
        // console.log('Permission Map', this.sidebarMenuPermission);

        this.generateDynamicNavigation();
      } else {
        // console.log('MENUS category not found');
        // Fallback to static menus if dynamic loading fails
        // this.dynamicNavigationItems = this.getStaticNavigationItems();
      }
    } catch (error) {
      console.error('Error loading dynamic menus:', error);
      // Fallback to static menus
      // this.dynamicNavigationItems = this.getStaticNavigationItems();
    } finally {
      this.isLoadingMenu = false;
    }
  }

  private generateDynamicNavigation(): void {
    const navigationItems: NavigationItem[] = [];

    // Filter parent menu items (ParentId is null) and sort by SortOrder
    const parentMenuItems = this.sidebarMenuPermission
      .filter(item => (item.ParentId == null) && this.canViewMenu(item) && item.HideMenu == 0)

    // Separate hidden settings that should appear in static area
    const regularMenuItems = parentMenuItems.filter(item => !this.isHiddenDynamicSetting(item));
    const staticSettingsItems = parentMenuItems.filter(item => this.isHiddenDynamicSetting(item));

    // Store dynamic settings for static area
    this.dynamicStaticSettings = staticSettingsItems.filter(item => this.canViewMenu(item));
    console.log('Dynamic Static Settings:', this.dynamicStaticSettings.map(i => ({ name: i.Name, displayName: i.DisplayName })));

    for (const menuItem of regularMenuItems) {
      const navItem: NavigationItem = {
        title: menuItem.DisplayName,
        icon: menuItem.NavIconClass?.startsWith('https://') ? menuItem.NavIconClass : this.getIconForMenu(menuItem.NavIconClass || menuItem.Name),
        route: menuItem.UrlRoute,
        children: []
      };

      // Process children if they exist
      if (menuItem.children && menuItem.children.length > 0) {
        const validChildren = menuItem.children
          .filter(child => this.canViewMenu(child) && child.HideMenu == 0)
          .sort((a, b) => a.SortOrder - b.SortOrder);

        navItem.children = validChildren.map(child => ({
          title: child.DisplayName,
          route: child.UrlRoute
        }));
      }

      // Only add items that have a valid route or children with valid routes
      if (navItem.route || (navItem.children && navItem.children.length > 0)) {
        navigationItems.push(navItem);
      }
    }

    this.dynamicNavigationItems = navigationItems;
    // console.log('Generated Navigation Items:', navigationItems);
    // console.log('Dynamic Static Settings:', this.dynamicStaticSettings);

    // Reinitialize feather icons after updating navigation
    setTimeout(() => {
      this.initializeFeatherIcons();
    }, 100);
  }

  private isHiddenDynamicSetting(menuItem: MenuItem): boolean {
    // Define which settings should be hidden from main navigation and shown as dynamic static settings
    // This can be modified based on your requirements - using Name property as identifier
    const hiddenSettings = ['M_SM_SETTINGS', 'M_SM_SETTINGS']; // Add the names of settings to hide

    return hiddenSettings.includes(menuItem.Name) ||
      (menuItem.ResourceTypeID === 999); // Or use any other identifier logic
  }

  private canViewMenu(menuItem: MenuItem): boolean {
    // First check if the menu item itself is active and not deleted
    if (!menuItem || Number(menuItem.IsActive) !== 1 || Number(menuItem.IsDeleted) === 1) {
      console.log(`Menu item ${menuItem?.Name} is inactive or deleted`, { IsActive: menuItem?.IsActive, IsDeleted: menuItem?.IsDeleted });
      return false;
    }

    // Check if permissions are defined
    if (!menuItem.resource_permission || menuItem.resource_permission.length === 0) {
      console.log(`No permissions defined for menu item ${menuItem.Name}`);
      return false;
    }

    // Check only the first permission (index 0)
    const firstPermission = menuItem.resource_permission[0];
    const canView = Number(firstPermission.CanView) === 1;
    const isActive = Number(firstPermission.IsActive) === 1;
    const isNotDeleted = Number(firstPermission.IsDeleted) === 0;

    if (menuItem.Name === 'M_SM_SETTINGS') {
      console.log(`Settings permission check (index 0):`, {
        permissionId: firstPermission.id,
        CanView: firstPermission.CanView,
        IsActive: firstPermission.IsActive,
        IsDeleted: firstPermission.IsDeleted,
        canView,
        isActive,
        isNotDeleted,
        result: canView && isActive && isNotDeleted
      });
    }

    const hasViewPermission = canView && isActive && isNotDeleted;
    return hasViewPermission;
  }

  public getIconForMenu(iconClass: string): string {
    // First try icon mapping
    const mappedIcon = this.iconMapping[iconClass];
    if (mappedIcon) {
      return mappedIcon;
    }

    // If provided NavIconClass exists and matches Feather's naming pattern (lowercase with hyphens)
    // Valid examples: 'home', 'shopping-cart', 'credit-card'
    // Invalid examples: 'PLAN_CHANGE', 'ADD_DATA_TOPUP'
    if (iconClass && /^[a-z]+(-[a-z]+)*$/.test(iconClass)) {
      return iconClass;
    }

    // Default fallback for invalid or missing icon names
    return 'circle';
  }

  private getStaticNavigationItems(): NavigationItem[] {
    return [
      {
        title: 'Dashboard',
        icon: 'home',
        route: '/customers/dashboard',
        isActive: true,
      },
      {
        title: 'Orders',
        icon: 'shopping-cart',
        route: '/orders',
        children: [
          { title: 'Order Review', route: '/orders/orders-review' },
          { title: 'Dropship Order', route: '/orders/dropship-orders' },
          { title: 'Awaiting Shipment', route: '/orders/awaiting-shipment' },
          { title: 'Order History', route: '/orders/shopify-orders' },
        ],
      },
      {
        title: 'Porting',
        icon: 'phone',
        route: '/porting',
      },
      {
        title: 'Subscription',
        icon: 'credit-card',
        route: '/subcription',
      },
      {
        title: 'Tickets',
        icon: 'help-circle',
        route: '/tickets',
      },
      {
        title: 'Bulk Process',
        icon: 'layers',
        route: '/bulk-process',
      },
      {
        title: 'Payments',
        icon: 'dollar-sign',
        route: '/payments',
      },
      {
        title: 'Reports',
        icon: 'bar-chart',
        route: '/reports',
      },
      {
        title: 'BI Boxes',
        icon: 'archive',
        route: '/bi-boxes',
      }
    ];
  }

  private initializeFeatherIcons(): void {
    // Check if feather is available globally
    if (typeof (window as any).feather !== 'undefined') {
      (window as any).feather.replace();
    } else {
      console.warn('Feather icons not loaded. Please include feather icons script in your index.html');
    }
  }

  get effectiveNavigationItems(): NavigationItem[] {
    return this.navigationItemsInput ?? this.dynamicNavigationItems;
  }

  handleMouseEnter(item: NavigationItem, index: number, event?: MouseEvent): void {
    // Clear any pending timeout to hide the menu
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }

    // Show submenu for items with children
    if (item.children && item.children.length > 0) {
      // Capture the actual viewport position of the hovered element for precise submenu placement
      if (event?.currentTarget) {
        const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
        this.submenuTopPosition = rect.top;
        this.submenuLeftPosition = rect.right + 8; // 8px gap from right edge of sidebar icon
      }

      // Force reset if hovering the same item to ensure submenu reopens
      if (this.hoveredItem === item) {
        this.hoveredItem = null;
        this.cdr.detectChanges(); // Force immediate detection of the null state
      }

      // Set the hovered state
      this.hoveredItem = item;
      this.hoveredItemIndex = index;
      // Manually trigger change detection to ensure Angular updates the view
      this.cdr.markForCheck();
    } else if (this.hoveredItem !== item) {
      // Clear hovered item only for items without children when switching
      this.hoveredItem = null;
      this.hoveredItemIndex = -1;
      this.cdr.markForCheck();
    }
  }

  getSubmenuTopPosition(index: number): number {
    // Calculate position based on icon position
    const baseTop = 130; // Initial padding
    const iconHeight = 55; // Height of each nav icon
    const iconSpacing = 0; // Gap between icons
    return baseTop + (index * (iconHeight + iconSpacing));
  }

  handleMouseLeave(): void {
    // Longer timeout to keep submenu visible when moving cursor
    this.hoverTimeout = setTimeout(() => {
      this.hoveredItem = null;
      this.hoveredItemIndex = -1;
      this.cdr.markForCheck();
    }, 400);
  }

  handleSubmenuEnter(): void {
    // Cancel any pending hide action when entering submenu
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }
    // Ensure the state remains set and trigger change detection
    this.cdr.markForCheck();
  }

  handleSubmenuLeave(): void {
    // Close submenu after short delay when mouse leaves submenu area
    this.hoverTimeout = setTimeout(() => {
      this.hoveredItem = null;
      this.hoveredItemIndex = -1;
      this.cdr.markForCheck();
    }, 200);
  }

  isActive(item: NavigationItem): boolean {
    // Exclude this route
    if (item.route === '/test-parent') {
      return false;
    }
    return this.router.url.startsWith(item.route);
  }

  shouldShowTooltip(item: NavigationItem): boolean {
    // Show tooltip for single-child menus and for leaf items
    if (item.children && item.children.length > 1) {
      return false;
    }
    return true;
  }

  getTooltipText(item: NavigationItem): string {
    if (item.children && item.children.length === 1) {
      return item.children[0]?.title || item.title;
    }
    return item.title;
  }

  isSubmenuOpen(item: NavigationItem): boolean {
    // Check if this item's submenu is currently open
    return this.hoveredItem === item;
  }

  getIconClasses(isActive?: boolean, item?: NavigationItem): string {
    const baseClasses = 'nav-icon-link';
    const isSubmenuVisible = item && this.isSubmenuOpen(item);
    return `${baseClasses} ${isActive || isSubmenuVisible ? 'active' : ''}`;
  }

  openSettings(): void {
    this.router.navigate(['/settings']);
  }

  openDynamicSetting(dynamicSetting: MenuItem): void {
    if (dynamicSetting.UrlRoute) {
      this.router.navigate([dynamicSetting.UrlRoute]);
    } else {
      console.warn('Dynamic setting has no route defined:', dynamicSetting);
      // Fallback to settings page if no route is defined
      this.router.navigate(['/settings']);
    }
  }

  isDynamicSettingActive(setting: MenuItem): boolean {
    const route = setting.UrlRoute || '/settings';
    return this.router.url.startsWith(route);
  }

  openDynamicSettingMobile(dynamicSetting: MenuItem): void {
    this.closeMobileSidebar();
    if (dynamicSetting.UrlRoute) {
      this.router.navigate([dynamicSetting.UrlRoute]);
    } else {
      this.router.navigate(['/settings']);
    }
  }

  onNavigationClick(event: Event, item: NavigationItem): void {
    event.preventDefault();
    event.stopPropagation();

    if (item.children && item.children.length === 1 && item.children[0]?.route) {
      this.router.navigate([item.children[0].route]);
      return;
    }

    this.router.navigate([item.route]);
  }

  ngOnDestroy(): void {
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }
  }

  // Mobile sidebar methods
  closeMobileSidebar(): void {
    this.closeRequest.emit();
  }

  onMobileSidebarClick(event: MouseEvent): void {
    // Check if click was on the sidebar background itself (not on content)
    const target = event.target as HTMLElement;
    if (target.classList.contains('mobile-sidebar')) {
      this.closeMobileSidebar();
    }
  }

  onMobileNavClick(item: NavigationItem): void {
    if (item.children && item.children.length > 1) {
      // Toggle accordion expansion
      if (this.expandedMobileItem() === item.title) {
        this.expandedMobileItem.set(null);
      } else {
        this.expandedMobileItem.set(item.title);
      }
      // Re-initialize feather icons for newly visible items
      setTimeout(() => this.initializeFeatherIcons(), 50);
    } else {
      // Navigate and close sidebar
      if (item.children && item.children.length === 1 && item.children[0]?.route) {
        this.router.navigate([item.children[0].route]);
      } else {
        this.router.navigate([item.route]);
      }
      this.closeMobileSidebar();
    }
  }

  onMobileSubNavClick(): void {
    // Close sidebar after navigation
    this.closeMobileSidebar();
  }

  toggleDarkMode(): void {
    this.themeService.toggleTheme();
    // isDarkMode is now a computed signal that automatically updates
  }

  logout(): void {
    this.closeMobileSidebar();
    this.authNewService.logout();
  }

  getUserInitials(): string {
    const user = this.authNewService.user();
    const name = user?.name || '';
    const words = name.trim().split(' ').filter((word: string) => word.length > 0);

    if (words.length === 0) return 'U';
    if (words.length === 1) return words[0].charAt(0).toUpperCase();
    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  }

  getUserColor(): string {
    const user = this.authNewService.user();
    const name = user?.name || '';
    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
      '#DDA0DD', '#98D8E8', '#F7DC6F', '#BB8FCE', '#85C1E9'
    ];

    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  }
}

